/**
 * Admin inspection detail + lifecycle controls. Shows the contextual action(s)
 * for the current status and lets the admin drive (or cancel) the whole flow.
 */

import { useEffect, useState, useCallback } from 'react';
import {
  fetchInspection,
  quoteInspection,
  markInspectionPaid,
  scheduleInspection,
  startInspection,
  reportInspection,
  cancelInspection,
  BADGE_LABEL,
  STATUS_META,
  feeToSar,
  type Inspection,
  type InspectionOutcome,
} from '../../../services/inspectionService';

interface Props {
  inspectionId: string;
  onClose: () => void;
  onChanged: () => void;
}

const field = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#B7DDD7]';

export default function InspectionDetailModal({ inspectionId, onClose, onChanged }: Props) {
  const [insp, setInsp] = useState<Inspection | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  // form state
  const [feeSar, setFeeSar] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [inspector, setInspector] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [outcome, setOutcome] = useState<InspectionOutcome>('PASS');
  const [score, setScore] = useState('');
  const [reportNotes, setReportNotes] = useState('');
  const [reportPhotos, setReportPhotos] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchInspection(inspectionId);
      setInsp(res.data);
    } catch {
      setError('Could not load this inspection.');
    } finally {
      setLoading(false);
    }
  }, [inspectionId]);

  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<{ data: Inspection }>, successMsg: string) => {
    setBusy(true); setError(null); setMsg(null);
    try {
      const res = await fn();
      setInsp(res.data);
      setMsg(successMsg);
      onChanged();
    } catch {
      setError('Action failed - check the values and the inspection status, then retry.');
    } finally {
      setBusy(false);
    }
  };

  const photos: string[] = (() => {
    try { return insp?.reportPhotos ? JSON.parse(insp.reportPhotos) : []; } catch { return []; }
  })();

  const prop = insp?.property as { titleEn?: string | null; titleAr?: string | null; address?: string | null } | null | undefined;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between z-10">
          <h3 className="font-semibold text-gray-900">Inspection</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100"><i className="ri-close-line text-lg" /></button>
        </div>

        {loading || !insp ? (
          <div className="py-16 text-center text-gray-400"><i className="ri-loader-4-line animate-spin text-2xl" /></div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Summary */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold text-gray-900">{BADGE_LABEL[insp.requestedBadgeType]}</div>
                <div className="text-sm text-gray-500">
                  {insp.agent?.displayName || [insp.agent?.user?.firstName, insp.agent?.user?.lastName].filter(Boolean).join(' ') || insp.agent?.user?.email}
                  {insp.agent?.user?.phone ? ` · ${insp.agent.user.phone}` : ''}
                </div>
              </div>
              <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${STATUS_META[insp.status].color}`}>{STATUS_META[insp.status].label}</span>
            </div>

            <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 text-sm">
              <div className="font-medium text-gray-800">{prop?.titleEn || prop?.titleAr || insp.propertyId}</div>
              {prop?.address && <div className="text-xs text-gray-500">{prop.address}</div>}
              {insp.brokerNote && <div className="text-xs text-gray-500 mt-1">Broker note: {insp.brokerNote}</div>}
            </div>

            {/* Info grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
              <Info label="Fee" value={insp.feeAmount ? `${feeToSar(insp.feeAmount).toLocaleString()} SAR` : '-'} />
              <Info label="Payment" value={insp.paymentStatus} />
              <Info label="Scheduled" value={insp.scheduledAt ? new Date(insp.scheduledAt).toLocaleString() : '-'} />
              <Info label="Inspector" value={insp.assignedInspectorName || '-'} />
            </div>

            {error && <div className="rounded-lg bg-red-50 text-red-600 text-sm px-3 py-2">{error}</div>}
            {msg && <div className="rounded-lg bg-green-50 text-green-700 text-sm px-3 py-2">{msg}</div>}

            {/* ── Contextual actions ─────────────────────────────── */}
            {insp.status === 'REQUESTED' && (
              <Action title="Set the inspection fee">
                <div className="flex gap-2">
                  <input value={feeSar} onChange={(e) => setFeeSar(e.target.value)} type="number" min="1" placeholder="Fee in SAR (e.g. 500)" className={field} />
                  <button disabled={busy || !feeSar} onClick={() => run(() => quoteInspection(insp.id, Math.round(Number(feeSar) * 100)), 'Fee quoted.')} className="px-4 rounded-lg bg-[#004D40] text-white text-sm whitespace-nowrap disabled:opacity-50">Send quote</button>
                </div>
              </Action>
            )}

            {insp.status === 'QUOTED' && (
              <>
                <Action title="Confirm payment received">
                  <div className="flex gap-2">
                    <input value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} placeholder="Payment reference (optional)" className={field} />
                    <button disabled={busy} onClick={() => run(() => markInspectionPaid(insp.id, paymentRef || undefined), 'Payment recorded.')} className="px-4 rounded-lg bg-[#004D40] text-white text-sm whitespace-nowrap disabled:opacity-50">Mark paid</button>
                  </div>
                </Action>
                <Action title="Adjust the fee">
                  <div className="flex gap-2">
                    <input value={feeSar} onChange={(e) => setFeeSar(e.target.value)} type="number" min="1" placeholder={`Current: ${feeToSar(insp.feeAmount)} SAR`} className={field} />
                    <button disabled={busy || !feeSar} onClick={() => run(() => quoteInspection(insp.id, Math.round(Number(feeSar) * 100)), 'Fee updated.')} className="px-4 rounded-lg border border-gray-200 text-sm whitespace-nowrap disabled:opacity-50">Re-quote</button>
                  </div>
                </Action>
              </>
            )}

            {(insp.status === 'PAID' || insp.status === 'SCHEDULED') && (
              <Action title={insp.status === 'PAID' ? 'Schedule the inspector' : 'Reschedule'}>
                <div className="space-y-2">
                  <input value={inspector} onChange={(e) => setInspector(e.target.value)} placeholder="Inspector name" className={field} />
                  <input value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} type="datetime-local" className={field} />
                  <button disabled={busy || !inspector || !scheduledAt} onClick={() => run(() => scheduleInspection(insp.id, inspector, new Date(scheduledAt).toISOString()), 'Inspector scheduled.')} className="px-4 py-2 rounded-lg bg-[#004D40] text-white text-sm disabled:opacity-50">{insp.status === 'PAID' ? 'Schedule' : 'Update schedule'}</button>
                </div>
              </Action>
            )}

            {insp.status === 'SCHEDULED' && (
              <Action title="Mark the visit underway">
                <button disabled={busy} onClick={() => run(() => startInspection(insp.id), 'Marked in progress.')} className="px-4 py-2 rounded-lg border border-gray-200 text-sm disabled:opacity-50">Start inspection</button>
              </Action>
            )}

            {(insp.status === 'SCHEDULED' || insp.status === 'IN_PROGRESS') && (
              <Action title="Record the field report">
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <select value={outcome} onChange={(e) => setOutcome(e.target.value as InspectionOutcome)} className={field}>
                      <option value="PASS">PASS - grant the badge</option>
                      <option value="CONDITIONAL">CONDITIONAL - grant with notes</option>
                      <option value="FAIL">FAIL - do not grant</option>
                    </select>
                    <input value={score} onChange={(e) => setScore(e.target.value)} type="number" min="0" max="100" placeholder="Score /100" className={`${field} max-w-[120px]`} />
                  </div>
                  <textarea value={reportNotes} onChange={(e) => setReportNotes(e.target.value)} rows={3} placeholder="Report notes…" className={field} />
                  <textarea value={reportPhotos} onChange={(e) => setReportPhotos(e.target.value)} rows={2} placeholder="Photo URLs (one per line, optional)" className={field} />
                  <button
                    disabled={busy}
                    onClick={() => run(() => reportInspection(insp.id, {
                      outcome,
                      score: score ? Number(score) : undefined,
                      reportNotes: reportNotes || undefined,
                      reportPhotos: reportPhotos.split(/\n+/).map((s) => s.trim()).filter(Boolean),
                    }), outcome === 'FAIL' ? 'Report saved (no badge).' : 'Report saved - badge verification added; approve it on the Badges screen.')}
                    className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm disabled:opacity-50"
                  >
                    Submit report
                  </button>
                </div>
              </Action>
            )}

            {/* Completed read-only */}
            {insp.status === 'COMPLETED' && (
              <div className="rounded-xl border border-green-100 bg-green-50/50 p-3 text-sm">
                <div className="font-medium text-gray-800">Outcome: {insp.outcome}{insp.score != null ? ` · ${insp.score}/100` : ''}</div>
                {insp.reportNotes && <p className="text-gray-600 mt-1">{insp.reportNotes}</p>}
                {photos.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {photos.map((u, i) => <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="text-xs text-[#003930] underline">photo {i + 1}</a>)}
                  </div>
                )}
                {insp.brokerBadgeId && (insp.outcome === 'PASS' || insp.outcome === 'CONDITIONAL') && (
                  <div className="mt-2 text-xs text-gray-600">A FIELD_INSPECTION verification was added to the broker's badge application. Approve it on the Badges admin screen to grant the badge.</div>
                )}
              </div>
            )}

            {insp.status === 'CANCELLED' && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
                Cancelled{insp.cancellationReason ? `: ${insp.cancellationReason}` : '.'}
              </div>
            )}

            {/* Cancel (any non-terminal status) */}
            {insp.status !== 'COMPLETED' && insp.status !== 'CANCELLED' && (
              <Action title="Cancel this inspection" danger>
                <div className="flex gap-2">
                  <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Reason" className={field} />
                  <button disabled={busy || !cancelReason} onClick={() => run(() => cancelInspection(insp.id, cancelReason), 'Inspection cancelled.')} className="px-4 rounded-lg bg-red-500 text-white text-sm whitespace-nowrap disabled:opacity-50">Cancel</button>
                </div>
              </Action>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-100 px-2.5 py-1.5">
      <div className="text-[11px] text-gray-400">{label}</div>
      <div className="text-gray-800 truncate">{value}</div>
    </div>
  );
}

function Action({ title, children, danger }: { title: string; children: React.ReactNode; danger?: boolean }) {
  return (
    <div className={`rounded-xl border p-3 ${danger ? 'border-red-100 bg-red-50/40' : 'border-gray-200'}`}>
      <div className={`text-sm font-medium mb-2 ${danger ? 'text-red-600' : 'text-gray-800'}`}>{title}</div>
      {children}
    </div>
  );
}
